import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
      CContainer,
      CRow,
      CCol,
      CInputGroup,
      CFormInput,
      CDropdown,
      CDropdownToggle,
      CDropdownMenu,
      CDropdownItem,
      CBadge,
      CButton,
      CCard,
      CCardImage,
      CCardBody,
      CCardTitle,
      CSpinner,
      CPagination,
      CPaginationItem
} from '@coreui/react';
import MarketplaceService from '../../../services/MarketplaceService';
import ArtworkCard from '../../../components/ArtworkCard';
import ArtworkDetailModal from '../../../components/ArtworkDetailModal';
import { toast } from 'react-toastify';
//import '../../scss/main.scss';
import Logger from '../../../utils/Logger';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { FixedSizeGrid as VirtualGrid } from 'react-window';


const TECH_CATEGORIES = {
      ALL: 'All Categories',
      ROBOTICS: 'Robotics',
      BLOCKCHAIN: 'Blockchain',
      THREE_D_PRINTING: '3D Printing',
      AI: 'Artificial Intelligence',
      IOT: 'Internet of Things',
      SOFTWARE: 'Software',
      HARDWARE: 'Hardware'
};

const SORT_OPTIONS = {
      NEWEST: 'Newest',
      PRICE_LOW: 'Price: Low to High',
      PRICE_HIGH: 'Price: High to Low',
      POPULARITY: 'Most Popular'
};

const MiEMarket = () => {
      const navigate = useNavigate();
      const location = useLocation();

      const [listings, setListings] = useState([]);
      const [topNFTs, setTopNFTs] = useState([]);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(null);
      const [searchTerm, setSearchTerm] = useState('');
      const [selectedCategory, setSelectedCategory] = useState(TECH_CATEGORIES.ALL);
      const [sortBy, setSortBy] = useState(SORT_OPTIONS.NEWEST);
      const [selectedNFT, setSelectedNFT] = useState(null);
      const [showDetailModal, setShowDetailModal] = useState(false);
      const [currentPage, setCurrentPage] = useState(1);
      const [totalPages, setTotalPages] = useState(1);
      const [itemsPerPage] = useState(10);
      const [sortOrder, setSortOrder] = useState('asc');
      const [priceRange, setPriceRange] = useState({ min: '', max: '' });
      const [totalItems, setTotalItems] = useState(0);
      const [gridWidth, setGridWidth] = useState(1200);
      const gridContainerRef = useRef(null);

      const marketplaceService = new MarketplaceService();

      useEffect(() => {
            fetchListings();
            //fetchTopNFTs();
      }, []);

      useEffect(() => {
            const container = gridContainerRef.current;
            if (!container) return undefined;

            const updateWidth = () => {
                  setGridWidth(Math.max(320, container.clientWidth || 1200));
            };
            updateWidth();

            const observer = new ResizeObserver(updateWidth);
            observer.observe(container);
            return () => observer.disconnect();
      }, []);

      const fetchListings = async (page = currentPage) => {
            try {
                  setLoading(true);
                  setError(null);

                  const response = await marketplaceService.getListings({
                        page: parseInt(page),
                        limit: parseInt(itemsPerPage),
                        sortBy: 'price',
                        sortOrder: 'desc',
                        minPrice: priceRange.min || undefined,
                        maxPrice: priceRange.max || undefined,
                        status: 'LISTED',
                        isPublic: true
                  });

                  // Destructure the response properly
                  const { listings, total, totalPages } = response;
                  setTotalItems(total || 0); // Ensure we have a default value

                  const listingsWithTags = listings.map(listing => ({
                        ...listing,
                        tags: Array.isArray(listing.tags) ? listing.tags : []
                  }));

                  setListings(listingsWithTags);
                  setTotalPages(totalPages || 1); // Ensure we have a default value
                  setCurrentPage(page);

            } catch (error) {
                  console.error('Error fetching listings:', error);
                  setError('Failed to fetch marketplace listings');
                  toast.error('Failed to load marketplace items');
            } finally {
                  setLoading(false);
            }
      };

      const fetchTopNFTs = async () => {
            try {
                  const response = await marketplaceService.getLatestIPFSMappings();
                  setTopNFTs(response.slice(0, 3)); // Get only the top 3
            } catch (error) {
                  console.error('Error fetching top NFTs:', error);
            }
      };

      const handleViewNFT = (nft) => {
            setSelectedNFT(nft);
            setShowDetailModal(true);
            // Update URL to use listingId instead of nftId
            navigate(`/marketplace/listing/${nft.listingID}`, {
                  replace: true,
                  state: { nft }
            });
      };

      const showAllBlockchain = () => {
            setSelectedCategory(TECH_CATEGORIES.BLOCKCHAIN);
            setSearchTerm('');
      };

      const handleSortChange = (option) => {
            switch (option) {
                  case SORT_OPTIONS.PRICE_LOW:
                        setSortBy('price');
                        setSortOrder('asc');
                        break;
                  case SORT_OPTIONS.PRICE_HIGH:
                        setSortBy('price');
                        setSortOrder('desc');
                        break;
                  case SORT_OPTIONS.NEWEST:
                        setSortBy('listedAt');
                        setSortOrder('desc');
                        break;
                  case SORT_OPTIONS.POPULARITY:
                        setSortBy('views');
                        setSortOrder('desc');
                        break;
                  default:
                        setSortBy('listedAt');
                        setSortOrder('desc');
            }
            fetchListings(1); // Reset to first page when sorting changes
      };

      useEffect(() => {
            fetchListings(1); // Reset to first page when filters change
      }, [searchTerm, selectedCategory, sortBy, sortOrder]);

      const handlePageChange = (newPage) => {
            Logger.debug('Handling page change:', {
                  currentPage,
                  newPage,
                  totalPages
            });

            if (newPage >= 1 && newPage <= totalPages) {
                  setCurrentPage(newPage);
                  fetchListings(newPage);
            }
      };

      const calculateItemRange = () => {
            if (!totalItems) return "0-0 of 0";

            const start = (currentPage - 1) * itemsPerPage + 1;
            const end = Math.min(currentPage * itemsPerPage, totalItems);
            return `${start}-${end} of ${totalItems}`;
      };

      // Function to generate shareable link
      const getShareableLink = (listing) => {
            return `${window.location.origin}/marketplace/listing/${listing.listingID}`;
      };

      // Add copy link button to modal
      const renderShareButton = (nftId) => {
            return (
                  <CButton
                        color="info"
                        size="sm"
                        onClick={() => {
                              const link = getShareableLink(nftId);
                              navigator.clipboard.writeText(link);
                              toast.success('Link copied to clipboard!');
                        }}
                  >
                        <i className="fas fa-share-alt me-2"></i>
                        Share
                  </CButton>
            );
      };

      const useVirtualizedListings = listings.length > 40;
      const gridColumnCount = useMemo(() => {
            if (gridWidth >= 1200) return 4;
            if (gridWidth >= 992) return 3;
            if (gridWidth >= 768) return 2;
            return 1;
      }, [gridWidth]);
      const gridRowCount = useMemo(
            () => Math.ceil(listings.length / gridColumnCount),
            [listings.length, gridColumnCount]
      );
      const gridColumnWidth = useMemo(
            () => Math.max(260, Math.floor(gridWidth / gridColumnCount)),
            [gridWidth, gridColumnCount]
      );

      const ListingGridCell = ({ columnIndex, rowIndex, style }) => {
            const index = rowIndex * gridColumnCount + columnIndex;
            const nft = listings[index];
            if (!nft) return null;

            let imageUrl = nft.imageUrl;
            if (!imageUrl && nft.metadata?.image) {
                  imageUrl = nft.metadata.image.startsWith('http')
                        ? nft.metadata.image
                        : `https://ipfs.mieros.cloud/ipfs/${nft.metadata.image}`;
            } else if (!imageUrl && nft.ipfsHash) {
                  imageUrl = `https://ipfs.mieros.cloud/ipfs/${nft.ipfsHash}`;
            }

            return (
                  <div style={{ ...style, padding: '0.5rem' }}>
                        <ArtworkCard
                              key={nft.nftId || nft.id}
                              isMarketplace={true}
                              title={nft.metadata?.title || nft.title || nft.itemType}
                              description={nft.description || nft.itemDetail}
                              imageUrl={imageUrl}
                              price={nft.price}
                              listingID={nft.listingID}
                              itemType={nft.itemType}
                              itemSubject={nft.itemSubject}
                              itemMedia={nft.itemMedia}
                              sellerID={nft.sellerID}
                              status={nft.status}
                              createDAt={nft.createdAt}
                              onView={() => handleViewNFT(nft)}
                        />
                  </div>
            );
      };

      return (
            <CContainer className="marketplace-container">
                  {/* Hero Section */}
                  <div className="marketplace-hero mb-4">
                        <h1>Internet of Things NFT Marketplace</h1>
                        <p>Discover unique digital and physical assets in IoT, Robotics, Gaming, and 3D printing</p>
                        {/* <CButton 
                              color="primary"
                              size="lg"
                              className="mt-3"
                              onClick={showAllBlockchain}
                        >
                              View All MiEROS NFTs
                        </CButton> */}
                  </div>

                  {/* Top 3 Latest NFTs Section
                  <div className="latest-nfts mb-5">
                        <h2 className="mb-4">Latest NFTs</h2>
                        <CRow>
                              {topNFTs.map((nft, index) => (
                                    <CCol key={nft.id} md={4}>
                                          <CCard className="latest-nft-card h-100">
                                                <CCardImage 
                                                      orientation="top" 
                                                      src={`https://ipfs.mieros.cloud/ipfs/${nft.ipfsHash}`}
                                                />
                                                <CCardBody>
                                                      <CCardTitle>{nft.title || `NFT #${index + 1}`}</CCardTitle>
                                                      <div className="mb-2 small mb-2">
                                                            Created: {new Date(nft.createdAt).toLocaleDateString()}
                                                      </div>
                                                      <CButton 
                                                            color="primary" 
                                                            size="sm"
                                                            onClick={() => handleViewNFT(nft)}
                                                      >
                                                            View Details
                                                      </CButton>
                                                </CCardBody>
                                          </CCard>
                                    </CCol>
                              ))}
                        </CRow>
                  </div> */}

                  {/* Popular Categories */}
                  <div className="popular-categories mb-4">
                        <div className="d-flex gap-2 flex-wrap">
                              <CBadge
                                    color="primary"
                                    className="category-badge"
                                    onClick={() => setSelectedCategory(TECH_CATEGORIES.ALL)}
                                    style={{ cursor: 'pointer' }}
                              >
                                    All
                              </CBadge>
                              {Object.entries(TECH_CATEGORIES).map(([key, value]) => (
                                    key !== 'ALL' && (
                                          <CBadge
                                                key={key}
                                                color={value === TECH_CATEGORIES.BLOCKCHAIN ? 'warning' : 'info'}
                                                className="category-badge"
                                                onClick={() => setSelectedCategory(value)}
                                                style={{
                                                      cursor: 'pointer',
                                                      backgroundColor: selectedCategory === value ? '#187da0' : undefined
                                                }}
                                          >
                                                {value}
                                          </CBadge>
                                    )
                              ))}
                        </div>
                  </div>

                  {/* Search and Filters */}
                  <CRow className="mb-4">
                        <CCol md={6}>
                              <CInputGroup>
                                    <CFormInput
                                          placeholder="Search NFTs..."
                                          value={searchTerm}
                                          onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                              </CInputGroup>
                        </CCol>
                        <CCol md={3}>
                              <CDropdown className="w-100">
                                    <CDropdownToggle color="light">{selectedCategory}</CDropdownToggle>
                                    <CDropdownMenu>
                                          {Object.values(TECH_CATEGORIES).map(category => (
                                                <CDropdownItem
                                                      key={category}
                                                      onClick={() => setSelectedCategory(category)}
                                                >
                                                      {category}
                                                </CDropdownItem>
                                          ))}
                                    </CDropdownMenu>
                              </CDropdown>
                        </CCol>
                        <CCol md={3}>
                              <CDropdown className="w-100">
                                    <CDropdownToggle color="light">{sortBy}</CDropdownToggle>
                                    <CDropdownMenu>
                                          {Object.values(SORT_OPTIONS).map(option => (
                                                <CDropdownItem
                                                      key={option}
                                                      onClick={() => setSortBy(option)}
                                                >
                                                      {option}
                                                </CDropdownItem>
                                          ))}
                                    </CDropdownMenu>
                              </CDropdown>
                        </CCol>
                  </CRow>

                  {loading && (
                        <div className="text-center my-4">
                              <CSpinner />
                              <p>Loading marketplace items...</p>
                        </div>
                  )}

                  {error && (
                        <div className="text-center my-4 text-danger">
                              <p>{error}</p>
                              <CButton
                                    color="primary"
                                    onClick={fetchListings}
                              >
                                    Retry
                              </CButton>
                        </div>
                  )}

                  {/* NFT Grid */}
                  <div className="marketplace-grid" ref={gridContainerRef}>
                        {listings.length === 0 && !loading ? (
                              <div className="text-center my-4">
                                    <p>No items found in the marketplace</p>
                              </div>
                        ) : useVirtualizedListings ? (
                              <VirtualGrid
                                    columnCount={gridColumnCount}
                                    columnWidth={gridColumnWidth}
                                    height={880}
                                    rowCount={gridRowCount}
                                    rowHeight={430}
                                    width={gridWidth}
                              >
                                    {ListingGridCell}
                              </VirtualGrid>
                        ) : (
                              listings.map(nft => {
                                    // Logger.debug('NFT data:', nft);
                                    // Logger.debug('Listing ID :', nft.listingID);

                                    // Determine the correct image URL
                                    let imageUrl = nft.imageUrl;  // Try direct imageUrl first
                                    if (!imageUrl && nft.metadata?.image) {
                                          imageUrl = nft.metadata.image.startsWith('http')
                                                ? nft.metadata.image
                                                : `https://ipfs.mieros.cloud/ipfs/${nft.metadata.image}`;
                                    } else if (!imageUrl && nft.ipfsHash) {
                                          imageUrl = `https://ipfs.mieros.cloud/ipfs/${nft.ipfsHash}`;
                                    }

                                    return (
                                          <ArtworkCard
                                                key={nft.nftId || nft.id}
                                                isMarketplace={true}
                                                title={nft.metadata?.title || nft.title || nft.itemType}
                                                description={nft.description || nft.itemDetail}
                                                imageUrl={imageUrl}
                                                price={nft.price}
                                                listingID={nft.listingID}
                                                itemType={nft.itemType}
                                                itemSubject={nft.itemSubject}
                                                itemMedia={nft.itemMedia}
                                                sellerID={nft.sellerID}
                                                status={nft.status}
                                                createDAt={nft.createdAt}
                                                onView={() => handleViewNFT(nft)}
                                          />
                                    );
                              })
                        )}
                  </div>

                  {/* Updated Pagination Component */}
                  <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="pagination-info">
                              <small className="mb-2">
                                    Showing items {calculateItemRange()}
                              </small>
                        </div>
                        <CPagination aria-label="Page navigation">
                              <CPaginationItem
                                    aria-label="Previous"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                              >
                                    <span aria-hidden="true">&laquo;</span>
                              </CPaginationItem>

                              {/* Show current page and total pages */}
                              {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(pageNum => {
                                          // Show first page, last page, and pages around current page
                                          return pageNum === 1 ||
                                                pageNum === totalPages ||
                                                Math.abs(pageNum - currentPage) <= 1;
                                    })
                                    .map((pageNum, index, array) => (
                                          <React.Fragment key={pageNum}>
                                                {index > 0 && array[index - 1] !== pageNum - 1 && (
                                                      <CPaginationItem disabled>...</CPaginationItem>
                                                )}
                                                <CPaginationItem
                                                      active={currentPage === pageNum}
                                                      onClick={() => handlePageChange(pageNum)}
                                                >
                                                      {pageNum}
                                                </CPaginationItem>
                                          </React.Fragment>
                                    ))}

                              <CPaginationItem
                                    aria-label="Next"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                              >
                                    <span aria-hidden="true">&raquo;</span>
                              </CPaginationItem>
                        </CPagination>
                  </div>

                  {/* Update Routes to use listing path */}
                  <Routes>
                        <Route 
                              path="/listing/:listingId" 
                              element={
                                    <ArtworkDetailModal
                                          show={showDetailModal}
                                          onHide={() => setShowDetailModal(false)}
                                          artwork={selectedNFT}
                                          isMarketplace={true}
                                    />
                              } 
                        />
                  </Routes>

                  {/* Update modal check for normal navigation */}
                  {!location.pathname.includes('/listing/') && (
                        <ArtworkDetailModal
                              show={showDetailModal}
                              onHide={() => setShowDetailModal(false)}
                              artwork={selectedNFT}
                              isMarketplace={true}
                        />
                  )}
            </CContainer>
      );
};

export default MiEMarket;


